import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import avatarOptions from "../data/avatarOptions";
import games from "../data/games";
import Seo from "../components/Seo";
import splashRegImage from "../assets/splashregimage.png";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.8-5.5 3.8-3.3 0-6-2.8-6-6.2s2.7-6.2 6-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.8 14.7 2 12 2 6.9 2 2.8 6.3 2.8 11.6S6.9 21.2 12 21.2c6.9 0 8.6-4.9 8.6-7.4 0-.5 0-.8-.1-1.2H12z" />
    <path fill="#34A853" d="M3.7 7.3l3.2 2.3c.9-2 2.7-3.4 5.1-3.4 1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.8 14.7 2 12 2 8.4 2 5.3 4.1 3.7 7.3z" />
    <path fill="#A855F7" d="M12 21.2c2.6 0 4.9-.9 6.5-2.5l-3-2.4c-.8.6-1.9 1.1-3.4 1.1-3.9 0-5.3-2.6-5.5-3.8l-3.2 2.4C5.1 19.1 8.2 21.2 12 21.2z" />
    <path fill="#FBBC05" d="M3.3 16l3.2-2.4c-.2-.6-.3-1.2-.3-1.9s.1-1.3.3-1.9L3.3 7.3C2.6 8.6 2.2 10 2.2 11.6c0 1.6.4 3.1 1.1 4.4z" />
  </svg>
);

const toDateOnly = (d) => d.toISOString().slice(0, 10);
const dateFromDayString = (day) => new Date(`${day}T00:00:00`);
const diffDays = (a, b) => Math.floor((b - a) / (1000 * 60 * 60 * 24));
const clampPercent = (value, max) => {
  if (!max) return 0;
  return Math.max(0, Math.min(100, Math.round((value / max) * 100)));
};
const formatDuration = (totalSeconds) => {
  const sec = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};
const sortedCounterEntries = (counter) =>
  Object.entries(counter && typeof counter === "object" ? counter : {})
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);

const getAvatarUrlById = (avatarId) =>
  avatarOptions.find((a) => a.id === avatarId)?.url || avatarOptions[0].url;

const USERNAME_CHANGE_COOLDOWN_DAYS = 30;
const USERNAME_CHANGE_COOLDOWN_MS = USERNAME_CHANGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const timestampToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatCooldownDate = (value) =>
  value
    ? value.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      })
    : "";
const timestampToMs = (value) => {
  const date = timestampToDate(value);
  return date ? date.getTime() : 0;
};

const Account = () => {
  const location = useLocation();

  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);
  const [profileUsername, setProfileUsername] = useState("");
  const [selectedAvatarId, setSelectedAvatarId] = useState(avatarOptions[0].id);
  const [showAllBadges, setShowAllBadges] = useState(false);
  const usernameChangedAtDate = useMemo(
    () => timestampToDate(stats?.usernameChangedAt),
    [stats?.usernameChangedAt]
  );
  const usernameNextChangeAt = useMemo(() => {
    if (!usernameChangedAtDate) return null;
    return new Date(usernameChangedAtDate.getTime() + USERNAME_CHANGE_COOLDOWN_MS);
  }, [usernameChangedAtDate]);
  const canChangeUsername =
    !usernameNextChangeAt || Date.now() >= usernameNextChangeAt.getTime();
  const usernameChangeTip = canChangeUsername
    ? `Tip: You can change your username now. After this, the next rename will be available in ${USERNAME_CHANGE_COOLDOWN_DAYS} days.`
    : `Tip: Username can be changed once every ${USERNAME_CHANGE_COOLDOWN_DAYS} days. Next change: ${formatCooldownDate(
        usernameNextChangeAt
      )}.`;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get("mode");
    if (modeParam === "signin" || modeParam === "signup") {
      setMode(modeParam);
    }
  }, [location.search]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser || null);
      setError("");
      setInfo("");

      if (!nextUser) {
        setStats(null);
        setNeedsProfileSetup(false);
        setProfileUsername("");
        setSelectedAvatarId(avatarOptions[0].id);
        return;
      }

      await loadUserData(nextUser);
    });

    return () => unsub();
  }, []);

  const loadUserData = async (authUser) => {
    try {
      const ref = doc(db, "users", authUser.uid);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : {};

      const username = (data?.username || authUser.displayName || "").trim();
      const avatarId =
        data?.avatarId && avatarOptions.some((a) => a.id === data.avatarId)
          ? data.avatarId
          : avatarOptions[0].id;

      setProfileUsername(username);
      setSelectedAvatarId(avatarId);
      setNeedsProfileSetup(!username || !data?.avatarId);

      const now = new Date();
      const today = toDateOnly(now);
      const prevVisit = data?.lastVisitDate || "";
      let streak = Number(data?.streakCount) || 0;

      if (prevVisit !== today) {
        if (prevVisit) {
          const delta = diffDays(dateFromDayString(prevVisit), dateFromDayString(today));
          streak = delta === 1 ? streak + 1 : 1;
        } else {
          streak = 1;
        }
      }

      const longestStreak = Math.max(Number(data?.longestStreak) || 0, streak);

      const baseCreatedAt =
        data?.createdAt ||
        (authUser.metadata?.creationTime
          ? { seconds: Math.floor(new Date(authUser.metadata.creationTime).getTime() / 1000) }
          : serverTimestamp());

      await setDoc(
        ref,
        {
          email: authUser.email || "",
          username,
          avatarId,
          createdAt: baseCreatedAt,
          lastVisitDate: today,
          streakCount: streak,
          longestStreak,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      setStats({
        ...data,
        username,
        avatarId,
        usernameChangedAt: data?.usernameChangedAt || null,
        badgeUnlockedAt: data?.badgeUnlockedAt || {},
        createdAt: data?.createdAt || baseCreatedAt,
        lastVisitDate: today,
        streakCount: streak,
        longestStreak
      });
    } catch {
      setError("Could not load profile data.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (mode === "signin") {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      } else {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (e) {
      setError(e.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      setError(e.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!user) return;

    const username = profileUsername.trim();
    if (!username) {
      setError("Username is required");
      return;
    }
    const currentUsername = (stats?.username || user.displayName || "").trim();
    const isUsernameChanged = username !== currentUsername;
    if (isUsernameChanged && !canChangeUsername) {
      setError(
        `Username can be changed once every ${USERNAME_CHANGE_COOLDOWN_DAYS} days. Next change available on ${formatCooldownDate(
          usernameNextChangeAt
        )}.`
      );
      return;
    }

    setLoading(true);
    setError("");
    setInfo("");

    try {
      const avatarUrl = getAvatarUrlById(selectedAvatarId);
      await updateProfile(user, { displayName: username, photoURL: avatarUrl });
      const profilePatch = {
        username,
        avatarId: selectedAvatarId,
        email: user.email || "",
        updatedAt: serverTimestamp()
      };
      if (isUsernameChanged) {
        profilePatch.usernameChangedAt = serverTimestamp();
      }
      await setDoc(
        doc(db, "users", user.uid),
        profilePatch,
        { merge: true }
      );

      setNeedsProfileSetup(false);
      setStats((prev) => ({
        ...(prev || {}),
        username,
        avatarId: selectedAvatarId,
        usernameChangedAt:
          isUsernameChanged
            ? new Date()
            : prev?.usernameChangedAt || stats?.usernameChangedAt || null
      }));
      setInfo(needsProfileSetup ? "Profile completed." : "Profile updated.");
    } catch (e) {
      setError(e.message || "Could not save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setError("");
    setInfo("");
    try {
      await signOut(auth);
    } catch (e) {
      setError(e.message || "Could not sign out");
    }
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;
    setError("");
    setInfo("");
    try {
      await sendPasswordResetEmail(auth, user.email);
      setInfo("Password reset email sent.");
    } catch (e) {
      setError(e.message || "Could not send reset email");
    }
  };

  const recentSessions = useMemo(() => {
    const recents = Array.isArray(stats?.recentGames) ? stats.recentGames : [];
    return recents.slice(0, 5);
  }, [stats]);

  const favoriteGames = useMemo(() => {
    if (Array.isArray(stats?.favoriteGames) && stats.favoriteGames.length) {
      return stats.favoriteGames
        .filter((g) => g && typeof g === "object" && g.id && g.slug)
        .slice(0, 5);
    }
    return recentSessions
      .slice(0, 3)
      .map((g) => ({
        id: g.id,
        slug: g.slug,
        title: g.title,
        thumbnail: g.thumbnail || ""
      }))
      .filter((g) => g.id && g.slug);
  }, [stats, recentSessions]);

  const gamesPlayed = Number(stats?.totalPlays) || 0;
  const streakCount = Number(stats?.streakCount) || 0;
  const favoriteCount = Array.isArray(stats?.favoriteGames)
    ? stats.favoriteGames.length
    : favoriteGames.length;

  const registeredDays = useMemo(() => {
    const createdSeconds = stats?.createdAt?.seconds;
    if (!createdSeconds) return 0;
    const createdDate = new Date(createdSeconds * 1000);
    const now = new Date();
    return Math.max(1, diffDays(new Date(createdDate.toDateString()), new Date(now.toDateString())) + 1);
  }, [stats]);
  const gameById = useMemo(() => {
    const map = new Map();
    games.forEach((g) => map.set(g.id, g));
    return map;
  }, []);
  const recentCards = useMemo(
    () =>
      recentSessions
        .map((entry) => {
          const full = gameById.get(entry.id);
          return {
            ...entry,
            slug: entry.slug || full?.slug || "",
            title: entry.title || full?.title || "Game",
            thumbnail: entry.thumbnail || full?.thumbnail || "",
            category: full?.category || "",
            genres: Array.isArray(full?.genres) ? full.genres : []
          };
        })
        .filter((entry) => entry.slug),
    [recentSessions, gameById]
  );
  const totalPlaytimeSeconds = Number(stats?.totalPlaytimeSeconds) || 0;
  const averageSessionSeconds = gamesPlayed > 0 ? Math.round(totalPlaytimeSeconds / gamesPlayed) : 0;
  const topPlayedGame = useMemo(() => {
    const byGame = stats?.playtimeByGameId;
    if (!byGame || typeof byGame !== "object") return null;
    const [topId, seconds] = Object.entries(byGame).sort(
      (a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0)
    )[0] || [];
    if (!topId) return null;
    const full = gameById.get(topId);
    return {
      id: topId,
      title: full?.title || topId,
      slug: full?.slug || "",
      seconds: Number(seconds) || 0
    };
  }, [stats, gameById]);
  const preferredGenres = useMemo(() => {
    const fromStats = sortedCounterEntries(stats?.genrePlayCounts);
    if (fromStats.length > 0) return fromStats.slice(0, 5);

    const fallbackCounter = {};
    recentCards.forEach((session) => {
      (session.genres || []).forEach((genre) => {
        fallbackCounter[genre] = (fallbackCounter[genre] || 0) + 1;
      });
    });
    favoriteGames.forEach((fav) => {
      const full = gameById.get(fav.id);
      (full?.genres || []).forEach((genre) => {
        fallbackCounter[genre] = (fallbackCounter[genre] || 0) + 1;
      });
    });
    return sortedCounterEntries(fallbackCounter).slice(0, 5);
  }, [stats, recentCards, favoriteGames, gameById]);
  const preferredCategories = useMemo(() => {
    const fromStats = sortedCounterEntries(stats?.categoryPlayCounts);
    if (fromStats.length > 0) return fromStats.slice(0, 4);

    const fallbackCounter = {};
    recentCards.forEach((session) => {
      if (session.category) {
        fallbackCounter[session.category] = (fallbackCounter[session.category] || 0) + 1;
      }
    });
    return sortedCounterEntries(fallbackCounter).slice(0, 4);
  }, [stats, recentCards]);
  const badges = useMemo(() => {
    const genreCount = sortedCounterEntries(stats?.genrePlayCounts).length || preferredGenres.length;
    const categoryCount =
      sortedCounterEntries(stats?.categoryPlayCounts).length || preferredCategories.length;
    const topGamePlaytime = Number(topPlayedGame?.seconds) || 0;
    const base = [
      {
        id: "starter",
        label: "Starter",
        description: "Play your first game",
        icon: "🎮",
        current: gamesPlayed,
        target: 1,
        tier: "bronze"
      },
      {
        id: "collector",
        label: "Collector I",
        description: "Save 5 favorites",
        icon: "⭐",
        current: favoriteCount,
        target: 5,
        tier: "bronze"
      },
      {
        id: "collector-ii",
        label: "Collector",
        description: "Save 10 favorites",
        icon: "🏅",
        current: favoriteCount,
        target: 10,
        tier: "silver"
      },
      {
        id: "collector-iii",
        label: "Collector III",
        description: "Save 25 favorites",
        icon: "🏆",
        current: favoriteCount,
        target: 25,
        tier: "gold"
      },
      {
        id: "streak-keeper",
        label: "Streak I",
        description: "Maintain 3-day streak",
        icon: "🔥",
        current: streakCount,
        target: 3,
        tier: "bronze"
      },
      {
        id: "streak-keeper-ii",
        label: "Streak Keeper",
        description: "Maintain 7-day streak",
        icon: "🔥",
        current: streakCount,
        target: 7,
        tier: "gold"
      },
      {
        id: "streak-keeper-iii",
        label: "Streak Master",
        description: "Maintain 30-day streak",
        icon: "👑",
        current: streakCount,
        target: 30,
        tier: "gold"
      },
      {
        id: "explorer",
        label: "Genre Explorer",
        description: "Play across 4 genres",
        icon: "🧭",
        current: genreCount,
        target: 4,
        tier: "silver"
      },
      {
        id: "genre-master",
        label: "Genre Master",
        description: "Play across 7 genres",
        icon: "🌌",
        current: genreCount,
        target: 7,
        tier: "gold"
      },
      {
        id: "category-rookie",
        label: "Category Rookie",
        description: "Play in 3 categories",
        icon: "🗂",
        current: categoryCount,
        target: 3,
        tier: "bronze"
      },
      {
        id: "category-nomad",
        label: "Category Nomad",
        description: "Play in 6 categories",
        icon: "🧳",
        current: categoryCount,
        target: 6,
        tier: "silver"
      },
      {
        id: "marathon-i",
        label: "Marathon I",
        description: "Reach 1 hour total playtime",
        icon: "⏱",
        current: totalPlaytimeSeconds,
        target: 1 * 3600,
        tier: "bronze",
        valueType: "time"
      },
      {
        id: "marathon-ii",
        label: "Marathon",
        description: "Reach 5 hours total playtime",
        icon: "⏱",
        current: totalPlaytimeSeconds,
        target: 5 * 3600,
        tier: "gold",
        valueType: "time"
      },
      {
        id: "marathon-iii",
        label: "Marathon III",
        description: "Reach 20 hours total playtime",
        icon: "⌛",
        current: totalPlaytimeSeconds,
        target: 20 * 3600,
        tier: "gold",
        valueType: "time"
      },
      {
        id: "dedicated-player",
        label: "Dedicated Player",
        description: "Play 25 games",
        icon: "🎯",
        current: gamesPlayed,
        target: 25,
        tier: "silver"
      },
      {
        id: "game-veteran",
        label: "Game Veteran",
        description: "Play 100 games",
        icon: "⚔",
        current: gamesPlayed,
        target: 100,
        tier: "gold"
      },
      {
        id: "returning-player",
        label: "Returning Player",
        description: "Account active for 7 days",
        icon: "📅",
        current: registeredDays,
        target: 7,
        tier: "bronze"
      },
      {
        id: "loyal-player",
        label: "Loyal Player",
        description: "Account active for 30 days",
        icon: "📆",
        current: registeredDays,
        target: 30,
        tier: "silver"
      },
      {
        id: "legendary-loyal",
        label: "Legendary Loyal",
        description: "Account active for 180 days",
        icon: "🛡",
        current: registeredDays,
        target: 180,
        tier: "gold"
      },
      {
        id: "one-game-focus",
        label: "Focused Runner",
        description: "Spend 1 hour in one game",
        icon: "🎲",
        current: topGamePlaytime,
        target: 1 * 3600,
        tier: "silver",
        valueType: "time"
      }
    ];

    const badgeUnlockedAtMap =
      stats?.badgeUnlockedAt && typeof stats.badgeUnlockedAt === "object"
        ? stats.badgeUnlockedAt
        : {};
    const computed = base.map((item, index) => {
      const current = Math.max(0, Number(item.current) || 0);
      const target = Math.max(1, Number(item.target) || 1);
      const isTime = item.valueType === "time";
      const unlocked = current >= target;
      const unlockedAtMs = unlocked ? timestampToMs(badgeUnlockedAtMap[item.id]) : 0;
      return {
        ...item,
        unlocked,
        unlockedAtMs,
        baseOrder: index,
        progressCurrent: Math.min(current, target),
        progressTextCurrent: isTime ? formatDuration(current) : current,
        progressTextTarget: isTime ? formatDuration(target) : target,
        progressPct: clampPercent(current, target)
      };
    });
    return computed.sort((a, b) => {
      if (a.unlocked && b.unlocked) {
        if (a.unlockedAtMs !== b.unlockedAtMs) return b.unlockedAtMs - a.unlockedAtMs;
        return a.baseOrder - b.baseOrder;
      }
      if (a.unlocked) return -1;
      if (b.unlocked) return 1;
      return a.baseOrder - b.baseOrder;
    });
  }, [
    stats,
    preferredGenres.length,
    preferredCategories.length,
    topPlayedGame?.seconds,
    totalPlaytimeSeconds,
    favoriteCount,
    streakCount,
    gamesPlayed,
    registeredDays
  ]);
  useEffect(() => {
    if (!user || !stats || !Array.isArray(badges) || badges.length === 0) return;

    const currentUnlockMap =
      stats.badgeUnlockedAt && typeof stats.badgeUnlockedAt === "object" ? stats.badgeUnlockedAt : {};
    const newUnlockMap = { ...currentUnlockMap };
    let hasNewUnlock = false;
    const nowIso = new Date().toISOString();

    badges.forEach((badge) => {
      if (badge.unlocked && !newUnlockMap[badge.id]) {
        newUnlockMap[badge.id] = nowIso;
        hasNewUnlock = true;
      }
    });

    if (!hasNewUnlock) return;

    setStats((prev) => ({
      ...(prev || {}),
      badgeUnlockedAt: newUnlockMap
    }));

    setDoc(
      doc(db, "users", user.uid),
      {
        badgeUnlockedAt: newUnlockMap,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    ).catch(() => {});
  }, [badges, stats, user]);
  const unlockedBadgesCount = useMemo(
    () => badges.filter((badge) => badge.unlocked).length,
    [badges]
  );
  const remainingBadgesCount = Math.max(0, badges.length - unlockedBadgesCount);
  const visibleBadges = badges.slice(0, 4);

  const authPreviewRecents = useMemo(
    () => games.slice(0, 4).map((g) => g.title).filter(Boolean),
    []
  );
  const isSignupMode = mode === "signup";
  const authFormTitle = isSignupMode ? "Create your account" : "Welcome back";
  const authFormSubtitle = isSignupMode
    ? "Sign up to save favorites, track sessions, and personalize your player profile."
    : "Sign in to continue with your favorites, recent sessions, and profile stats.";

  if (!user) {
    return (
      <main className="page page-account">
        <Seo
          title={mode === "signup" ? "Create Account" : "Sign In"}
          description="Sign in to OctoGames to save favorites, track sessions, and sync your player profile."
          path={`/account${location.search || ""}`}
          noindex
        />
        <div className="account-shell">
          <div className="account-auth-layout">
            <section className="account-panel">
              <article className="account-card-form">
                <h1 className="account-title">{authFormTitle}</h1>
                <p className="account-subtitle">
                  {authFormSubtitle}
                </p>

                <div className="account-sso-row">
                  <button
                    type="button"
                    className="account-sso-btn account-sso-btn-primary"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    aria-label="Continue with Google"
                  >
                    <GoogleIcon />
                  </button>
                </div>

                <div className="account-or">
                  <span className="account-or-line" />
                  <span className="account-or-label">Or</span>
                  <span className="account-or-line" />
                </div>

                <form className="account-form" onSubmit={handleSubmit}>
                  <label className="account-label">
                    Email
                    <input
                      className="account-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                  </label>

                  <label className="account-label">
                    Password
                    <input
                      className="account-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </label>

                  {error && <p className="account-error">{error}</p>}
                  {info && <p className="account-info">{info}</p>}

                  <button type="submit" className="account-submit" disabled={loading}>
                    {loading
                      ? "Please wait..."
                      : mode === "signin"
                        ? "Sign in"
                        : "Create account"}
                  </button>
                </form>

                <button
                  type="button"
                  className="account-switch"
                  onClick={() => setMode((m) => (m === "signin" ? "signup" : "signin"))}
                >
                  {mode === "signin"
                    ? "No account yet? Sign up"
                    : "Already have an account? Sign in"}
                </button>
              </article>
            </section>

            <section className="account-panel">
              <article className="account-hero">
                <div className="account-hero-media">
                  <img
                    src={splashRegImage}
                    alt="OctoGames splash"
                    className="account-hero-img"
                  />
                  <div className="account-hero-gradient" />
                  <div className="account-hero-badge">
                    <div className="account-hero-badge-main">840+</div>
                    <div className="account-hero-badge-sub">Games available</div>
                  </div>
                  <div className="account-hero-floating-brand">OctoGames</div>
                </div>

                <div className="account-hero-content">
                  <h2 className="account-hero-title">Play. Save. Track.</h2>
                  <p className="account-hero-subtitle">
                    Sign in to sync favorites, recent sessions, and your profile.
                  </p>
                  <div className="account-hero-tags">
                    {["Instant Play", "No Install", "Daily Updates", "Favorites"].map(
                      (tag, index) => (
                        <span
                          key={tag}
                          className={`account-hero-tag color-${index % 4}`}
                        >
                          {tag}
                        </span>
                      )
                    )}
                  </div>
                  <div className="account-hero-recents">
                    <span className="account-hero-recents-label">Trending:</span>
                    <div className="account-hero-recents-list">
                      {authPreviewRecents.map((title) => (
                        <span key={title} className="account-hero-recent-pill">
                          {title}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </section>
          </div>
        </div>
      </main>
    );
  }

  if (needsProfileSetup) {
    return (
      <main className="page page-account">
        <Seo
          title="Complete Profile"
          description="Complete your OctoGames profile by choosing a username and avatar."
          path="/account"
          noindex
        />
        <div className="account-page-shell">
          <section className="account-auth-card-new">
            <h1 className="account-auth-title">Complete your profile</h1>
            <p className="account-auth-subtitle">
              Choose username and avatar. This step is required.
            </p>

            <form className="account-form" onSubmit={handleSaveProfile}>
              <label className="account-label">
                Username
                <input
                  className="account-input"
                  type="text"
                  value={profileUsername}
                  onChange={(e) => setProfileUsername(e.target.value)}
                  placeholder="OctoPlayer"
                  required
                />
              </label>
              <p className="account-hint">
                Tip: You can update your username once every {USERNAME_CHANGE_COOLDOWN_DAYS} days.
              </p>

              <div className="account-avatar-grid">
                {avatarOptions.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    className={
                      "account-avatar-option" +
                      (selectedAvatarId === avatar.id ? " account-avatar-option-active" : "")
                    }
                    onClick={() => setSelectedAvatarId(avatar.id)}
                    aria-label={`Select ${avatar.id}`}
                  >
                    <img src={avatar.url} alt={avatar.id} className="account-avatar-img" />
                  </button>
                ))}
              </div>

              {error && <p className="account-error">{error}</p>}
              {info && <p className="account-info">{info}</p>}

              <button type="submit" className="account-submit" disabled={loading}>
                {loading ? "Saving..." : "Save profile"}
              </button>
            </form>

            <button type="button" className="account-switch" onClick={handleSignOut}>
              Sign out
            </button>
          </section>
        </div>
      </main>
    );
  }

  const avatarUrl = getAvatarUrlById(stats?.avatarId || "") || user.photoURL || "";
  const renderBadgeCard = (badge) => (
    <article
      key={badge.id}
      className={
        "profile-badge-card" +
        ` profile-badge-${badge.tier}` +
        (badge.unlocked ? " profile-badge-card-unlocked" : "")
      }
    >
      <div className="profile-badge-head">
        <div className="profile-badge-medal">{badge.icon}</div>
        <div className="profile-badge-main">
          <div className="profile-badge-title-row">
            <h3 className="profile-badge-title">{badge.label}</h3>
            <span className="profile-badge-tier">{badge.tier}</span>
          </div>
          <p className="profile-badge-copy">{badge.description}</p>
        </div>
      </div>
      <div className="profile-badge-progress">
        <div className="profile-badge-progress-track" aria-hidden="true">
          <span style={{ width: `${badge.progressPct}%` }} />
        </div>
        <span className="profile-badge-progress-value">
          {badge.progressTextCurrent} / {badge.progressTextTarget}
        </span>
      </div>
      <span
        className={
          "profile-badge-status" + (badge.unlocked ? " profile-badge-status-unlocked" : "")
        }
      >
        {badge.unlocked ? "Unlocked" : "In progress"}
      </span>
    </article>
  );

  return (
    <main className="page page-account">
      <Seo
        title="My Account"
        description="Manage your OctoGames profile, favorite games, and recent sessions."
        path="/account"
        noindex
      />
      <div className="profile-shell">
        <section className="profile-header-card">
          <div className="profile-main">
            <img src={avatarUrl} alt="Player avatar" className="profile-avatar" />
            <div className="profile-main-meta">
              <h1 className="profile-username">{stats?.username || user.displayName || "Player"}</h1>
              <p className="profile-email">{user.email}</p>
            </div>
          </div>
          <div className="profile-actions">
            <button type="button" className="profile-action-btn" onClick={handleChangePassword}>
              Change password
            </button>
            <button type="button" className="profile-action-btn profile-action-signout" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </section>

        {(error || info) && (
          <section className="profile-note-card">
            {error && <p className="account-error">{error}</p>}
            {info && <p className="account-info">{info}</p>}
          </section>
        )}

        <section className="profile-stats-grid">
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Games Played</h3>
            <p className="profile-stat-value">{gamesPlayed}</p>
          </article>
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Favorite Games</h3>
            <p className="profile-stat-value">{favoriteCount}</p>
          </article>
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Registered</h3>
            <p className="profile-stat-value">{registeredDays} day(s)</p>
          </article>
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Streak</h3>
            <p className="profile-stat-value">{streakCount} day(s)</p>
          </article>
        </section>

        <section className="profile-playtime-grid">
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Total Playtime</h3>
            <p className="profile-stat-value">{formatDuration(totalPlaytimeSeconds)}</p>
          </article>
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Avg Session</h3>
            <p className="profile-stat-value">{formatDuration(averageSessionSeconds)}</p>
          </article>
          <article className="profile-stat-card">
            <h3 className="profile-stat-label">Top Played Game</h3>
            <p className="profile-stat-value profile-stat-value-sm">
              {topPlayedGame?.slug ? (
                <Link to={`/game/${topPlayedGame.slug}`} className="profile-inline-link">
                  {topPlayedGame.title}
                </Link>
              ) : (
                "No data yet"
              )}
            </p>
          </article>
        </section>

        <section className="profile-badges-wrap">
          <div className="profile-badges-top">
            <h2 className="profile-block-title">Achievements & Badges</h2>
            <div className="profile-badges-counter">
              <span className="profile-badges-count-achieved">
                Achieved: {unlockedBadgesCount}
              </span>
              <span className="profile-badges-count-left">
                Left: {remainingBadgesCount}
              </span>
            </div>
          </div>
          <div className="profile-badges-row">
            {visibleBadges.map(renderBadgeCard)}
          </div>
          <div className="profile-badges-actions">
            <button
              type="button"
              className="profile-action-btn"
              onClick={() => setShowAllBadges(true)}
            >
              Show all achievements ({badges.length})
            </button>
          </div>
        </section>

        <section className="profile-content-grid">
          <article className="profile-block">
            <h2 className="profile-block-title">Favorite Games</h2>
            {favoriteGames.length === 0 ? (
              <p className="profile-empty">No favorites yet.</p>
            ) : (
              <div className="profile-favorites-grid">
                {favoriteGames.map((game) => (
                  <Link
                    key={game.id}
                    to={`/game/${game.slug}`}
                    className="profile-favorite-card"
                  >
                    {game.thumbnail ? (
                      <img
                        src={game.thumbnail}
                        alt={game.title}
                        className="profile-favorite-img"
                      />
                    ) : (
                      <div className="profile-favorite-fallback">🎮</div>
                    )}
                    <span className="profile-favorite-title">{game.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </article>

          <article className="profile-block">
            <h2 className="profile-block-title">Category / Genre Preference</h2>
            {preferredCategories.length === 0 && preferredGenres.length === 0 ? (
              <p className="profile-empty">Play more games to unlock preference insights.</p>
            ) : (
              <>
                <div className="profile-pref-group">
                  <h3 className="profile-pref-title">Top Categories</h3>
                  <div className="profile-chip-wrap">
                    {preferredCategories.map((item) => (
                      <span key={item.name} className="profile-chip">
                        {item.name} ({item.value})
                      </span>
                    ))}
                  </div>
                </div>
                <div className="profile-pref-group">
                  <h3 className="profile-pref-title">Top Genres</h3>
                  <div className="profile-chip-wrap">
                    {preferredGenres.map((item) => (
                      <span key={item.name} className="profile-chip">
                        {item.name} ({item.value})
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </article>
        </section>

        <section className="profile-content-grid">
          <article className="profile-block">
            <h2 className="profile-block-title">Profile Edit Panel</h2>
            <form className="account-form" onSubmit={handleSaveProfile}>
              <label className="account-label">
                Username
                <input
                  className="account-input"
                  type="text"
                  value={profileUsername}
                  onChange={(e) => setProfileUsername(e.target.value)}
                  placeholder="OctoPlayer"
                  disabled={!canChangeUsername}
                  required
                />
              </label>
              <p className="account-hint">{usernameChangeTip}</p>

              <div className="account-avatar-grid">
                {avatarOptions.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    className={
                      "account-avatar-option" +
                      (selectedAvatarId === avatar.id ? " account-avatar-option-active" : "")
                    }
                    onClick={() => setSelectedAvatarId(avatar.id)}
                    aria-label={`Select ${avatar.id}`}
                  >
                    <img src={avatar.url} alt={avatar.id} className="account-avatar-img" />
                  </button>
                ))}
              </div>

              <button type="submit" className="account-submit" disabled={loading}>
                {loading ? "Saving..." : "Save profile changes"}
              </button>
            </form>
          </article>

          <article className="profile-block">
            <h2 className="profile-block-title">Recently Played</h2>
            {recentCards.length === 0 ? (
              <p className="profile-empty">No sessions yet. Play a game to start tracking.</p>
            ) : (
              <div className="profile-recent-grid">
                {recentCards.map((game) => (
                  <Link key={game.id} to={`/game/${game.slug}`} className="profile-recent-card">
                    {game.thumbnail ? (
                      <img
                        src={game.thumbnail}
                        alt={game.title}
                        className="profile-recent-img"
                      />
                    ) : (
                      <div className="profile-favorite-fallback">🎮</div>
                    )}
                    <div className="profile-recent-meta">
                      <span className="profile-recent-title">{game.title}</span>
                      <span className="profile-recent-sub">
                        {game.lastPlayedAt?.seconds
                          ? new Date(game.lastPlayedAt.seconds * 1000).toLocaleString()
                          : "Recently played"}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </article>
        </section>
      </div>
      {showAllBadges && (
        <div
          className="profile-badges-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="All achievements"
          onClick={() => setShowAllBadges(false)}
        >
          <div
            className="profile-badges-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-badges-modal-head">
              <div>
                <h2 className="profile-block-title">All Achievements</h2>
                <p className="profile-badges-modal-sub">
                  Achieved {unlockedBadgesCount}/{badges.length} · Left {remainingBadgesCount}
                </p>
              </div>
              <button
                type="button"
                className="profile-action-btn"
                onClick={() => setShowAllBadges(false)}
              >
                Close
              </button>
            </div>
            <div className="profile-badges-grid">
              {badges.map(renderBadgeCard)}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Account;

